/**
 * FillProjection — "the database is N-dimensional; look at it in the view YOU want"
 * (FXC-9009, the hero-demo projection panel).
 *
 * The user picks any two of the campaign's canonical axes; every axis the view
 * doesn't show gets a PIN slider (where to slice) under one SLAB slider (how
 * thick a slice counts as "on view"). Points project onto the picked plane with
 * off-slice distance shown honestly — ghosted, never silently flattened. A 3D
 * toggle hands the same picked axes to TrustSurfacePanel (its axisX/axisY are
 * generic), so the multi-source trust surface renders over any pair too.
 *
 * Pure and props-driven: campaign steps + declared axes in, no queries of its
 * own (the optional `three` prop carries what TrustSurfacePanel needs). During
 * a replay the caller passes the replay's FROZEN snapshot + cursor, so dots
 * land in the projection in the same chronology as the hero replay.
 */
import { useEffect, useMemo, useState } from "react";

import type { CampaignAxisSpec } from "../../../api/highliftCampaign";
import type { HighliftPalette } from "../foil/useHighliftTheme";
import { TrustSurfacePanel } from "../../thread-v5/TrustSurfacePanel";
import {
  FILL_AXES,
  STEP_AXIS_VALUE,
  projectStepsToView,
  type ReplayStep,
} from "./campaignMath";

const MONO = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";
const CELL_W = 300;
const CELL_H = 220;
const PAD = 14;

interface ViewAxis {
  canonical: string;
  label: string;
  fmt: (v: number) => string;
  min: number;
  max: number;
  ranged: boolean;
}

/** The pickable view axes: the campaign's declared axes in FILL_AXES order —
 *  range axes frame with their declared box; held axes frame with a ±span
 *  around the hold so points don't sit on a degenerate edge. */
export function projectionAxes(axes: CampaignAxisSpec[]): ViewAxis[] {
  const bySpec = new Map(axes.map((ax) => [ax.axis, ax]));
  return FILL_AXES.filter((f) => bySpec.has(f.canonical) && STEP_AXIS_VALUE[f.canonical] != null).map(
    (f) => {
      const spec = bySpec.get(f.canonical)!;
      if (
        spec.mode === "range" &&
        typeof spec.min === "number" &&
        typeof spec.max === "number" &&
        spec.max > spec.min
      ) {
        return { canonical: f.canonical, label: f.label, fmt: f.fmt, min: spec.min, max: spec.max, ranged: true };
      }
      const v = typeof spec.value === "number" ? spec.value : 0;
      const span = Math.max((f.range[1] - f.range[0]) * 0.1, Math.abs(v) * 0.1, 1e-3);
      return { canonical: f.canonical, label: f.label, fmt: f.fmt, min: v - span, max: v + span, ranged: false };
    },
  );
}

function frac(v: number, lo: number, hi: number): number {
  return Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
}

export function FillProjection({
  steps,
  upTo,
  axes,
  palette,
  three,
}: {
  steps: ReplayStep[];
  /** Replay cursor into `steps` (the caller passes the replay's frozen snapshot
   *  while replaying); null shows everything. */
  upTo?: number | null;
  axes: CampaignAxisSpec[];
  palette: HighliftPalette;
  /** Everything the 3D trust surface needs; omit to hide the 3D toggle. */
  three?: { projectId: string; branch: string; quantity: string; fixedBase: Record<string, number> } | null;
}) {
  const viewAxes = useMemo(() => projectionAxes(axes), [axes]);
  const ranged = viewAxes.filter((a) => a.ranged);
  const defaultX = (ranged[0] ?? viewAxes[0])?.canonical;
  const defaultY = (ranged[1] ?? viewAxes[1] ?? viewAxes[0])?.canonical;
  const [xAxis, setXAxis] = useState<string | undefined>(defaultX);
  const [yAxis, setYAxis] = useState<string | undefined>(defaultY);
  const [pins, setPins] = useState<Record<string, number>>({});
  const [slabPct, setSlabPct] = useState(25);
  const [mode, setMode] = useState<"2d" | "3d">("2d");
  // Debounced echo of the pins for the 3D trust surface ONLY: every distinct
  // `fixed` value is a new query key and a full server-side surface fit — a
  // slider drag must not enqueue one per pixel on the single-worker demo API.
  // The 2D projection keeps reading the LIVE pins (pure client math, free).
  const [pinsSettled, setPinsSettled] = useState<Record<string, number>>({});
  useEffect(() => {
    const timer = window.setTimeout(() => setPinsSettled(pins), 450);
    return () => window.clearTimeout(timer);
  }, [pins]);

  const x = viewAxes.find((a) => a.canonical === xAxis) ?? ranged[0] ?? viewAxes[0];
  const y = viewAxes.find((a) => a.canonical === yAxis && a.canonical !== x?.canonical)
    ?? viewAxes.find((a) => a.canonical !== x?.canonical);
  if (!x || !y || viewAxes.length < 2) return null;

  // The axes the view is NOT showing but the campaign ranged: those need pins.
  // Pins clamp to each axis's declared box on READ, so a pin left over from a
  // previously adopted campaign can never sit outside the current frame.
  const pinAxes = ranged.filter((a) => a.canonical !== x.canonical && a.canonical !== y.canonical);
  const clampPin = (a: { canonical: string; min: number; max: number }, raw: Record<string, number>) =>
    Math.min(a.max, Math.max(a.min, raw[a.canonical] ?? (a.min + a.max) / 2));
  const view = {
    x: x.canonical,
    y: y.canonical,
    pins: Object.fromEntries(pinAxes.map((a) => [a.canonical, clampPin(a, pins)])),
    slabFrac: slabPct / 100,
  };
  // What the 3D surface slices at: campaign HOLD values first (without them the
  // server would slice at schema defaults — wrong for any held-off-default box),
  // then the user's settled pins on top.
  const heldValues = Object.fromEntries(
    axes
      .filter((ax) => ax.mode === "hold" && typeof ax.value === "number" && STEP_AXIS_VALUE[ax.axis] != null)
      .map((ax) => [ax.axis, ax.value as number]),
  );
  const pins3d = Object.fromEntries(pinAxes.map((a) => [a.canonical, clampPin(a, pinsSettled)]));
  const projected = projectStepsToView(steps, axes, view);
  const visible = upTo == null ? projected : projected.slice(0, Math.max(0, upTo + 1));
  const cls = steps.map((s) => s.CL).filter((v): v is number => v != null);
  const clLo = cls.length ? Math.min(...cls) : 0;
  const clHi = cls.length ? Math.max(...cls) : 1;
  const onView = visible.filter((p) => p.offSlice === 0 && !p.step.failed).length;

  const chip = (label: string, active: boolean, onClick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      style={{
        fontSize: 9.5,
        fontFamily: MONO,
        padding: "2px 7px",
        borderRadius: 999,
        cursor: "pointer",
        border: `1px solid ${active ? palette.primary : palette.line}`,
        background: active ? palette.primarySoft : palette.surface2,
        color: active ? palette.primary : palette.ink3,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="fill-projection">
      {/* axis pickers + 2D/3D */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink4 }}>x</span>
        {viewAxes.map((a) =>
          chip(a.label, a.canonical === x.canonical, () => setXAxis(a.canonical), `x-${a.canonical}`),
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink4 }}>y</span>
        {viewAxes
          .filter((a) => a.canonical !== x.canonical)
          .map((a) => chip(a.label, a.canonical === y.canonical, () => setYAxis(a.canonical), `y-${a.canonical}`))}
        <span style={{ flex: 1 }} />
        {three ? (
          <>
            {chip("2D points", mode === "2d", () => setMode("2d"), "m2")}
            {chip("3D trust surface", mode === "3d", () => setMode("3d"), "m3")}
          </>
        ) : null}
      </div>

      {/* pins + slab for the axes the view hides */}
      {pinAxes.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {pinAxes.map((a) => {
            const v = view.pins[a.canonical]!;
            return (
              <label key={a.canonical} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 9.5, fontFamily: MONO, color: palette.ink3 }}>
                <span style={{ width: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  slice {a.label} @ {a.fmt(v)}
                </span>
                <input
                  type="range"
                  min={a.min}
                  max={a.max}
                  step={(a.max - a.min) / 100}
                  value={v}
                  onChange={(e) => setPins((prev) => ({ ...prev, [a.canonical]: Number(e.target.value) }))}
                  style={{ flex: 1 }}
                />
              </label>
            );
          })}
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 9.5, fontFamily: MONO, color: palette.ink3 }}>
            <span style={{ width: 110 }}>slab ±{slabPct}%</span>
            <input type="range" min={5} max={50} step={5} value={slabPct} onChange={(e) => setSlabPct(Number(e.target.value))} style={{ flex: 1 }} />
          </label>
        </div>
      ) : null}

      {mode === "3d" && three ? (
        <div style={{ height: 260 }}>
          <TrustSurfacePanel
            projectId={three.projectId}
            branch={three.branch}
            quantity={three.quantity}
            axisX={x.canonical}
            axisY={y.canonical}
            fixed={{ ...three.fixedBase, ...heldValues, ...pins3d }}
            fill
          />
        </div>
      ) : (
        <svg
          width="100%"
          viewBox={`0 0 ${CELL_W} ${CELL_H}`}
          // position/height inline: the workbench's global `.hlw-dock-b svg`
          // rule absolutizes descendant svgs to fill the dock (built for the
          // Cp strip) — left alone it would paint this cell OVER the axis
          // chips and sliders and swallow their clicks.
          style={{ position: "static", height: "auto", background: palette.surface2, borderRadius: 8, border: `1px solid ${palette.line}`, maxWidth: 430 }}
        >
          {visible.map((p, i) => {
            const px = PAD + frac(p.x, x.min, x.max) * (CELL_W - 2 * PAD);
            const py = CELL_H - PAD - frac(p.y, y.min, y.max) * (CELL_H - 2 * PAD);
            const isCurrent = upTo != null && i === upTo;
            if (p.step.failed) {
              return (
                <g key={i} stroke={palette.warn} strokeWidth={1.4} opacity={1 - 0.7 * p.offSlice}>
                  <line x1={px - 3.4} y1={py - 3.4} x2={px + 3.4} y2={py + 3.4} />
                  <line x1={px - 3.4} y1={py + 3.4} x2={px + 3.4} y2={py - 3.4} />
                </g>
              );
            }
            const t = p.step.CL == null || clHi === clLo ? 0.5 : frac(p.step.CL, clLo, clHi);
            // Ghost by off-slice distance: on-slab points read at full strength,
            // far points fade toward (but never reach) invisible — projection
            // shows what it moved, it doesn't hide it.
            const ghost = 1 - 0.8 * p.offSlice;
            const opacity = (0.4 + 0.6 * t) * ghost;
            const r = 4.2 - 1.6 * p.offSlice;
            return (
              <g key={i} data-offslice={p.offSlice > 0 ? "1" : "0"}>
                {p.step.cached ? (
                  <circle cx={px} cy={py} r={r} fill="none" stroke={palette.blue} strokeWidth={1.5} opacity={opacity} />
                ) : (
                  <circle cx={px} cy={py} r={r} fill={palette.blue} opacity={opacity} />
                )}
                {isCurrent ? (
                  <circle cx={px} cy={py} r={8} fill="none" stroke={palette.primary} strokeWidth={1.5}>
                    <animate attributeName="r" values="6;10;6" dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="1;0.25;1" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                ) : null}
              </g>
            );
          })}
          <text x={CELL_W / 2} y={CELL_H - 3} textAnchor="middle" fontSize={9} fontFamily={MONO} fill={palette.ink3}>
            {x.label}
          </text>
          <text x={8} y={CELL_H / 2} textAnchor="middle" fontSize={9} fontFamily={MONO} fill={palette.ink3} transform={`rotate(-90 8 ${CELL_H / 2})`}>
            {y.label}
          </text>
        </svg>
      )}

      <div style={{ fontSize: 9.5, fontFamily: MONO, color: palette.ink3, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <span>
          {onView}/{visible.filter((p) => !p.step.failed).length} landed points on this view
          {pinAxes.length > 0 ? ` · ${pinAxes.length} axis${pinAxes.length === 1 ? "" : "es"} sliced` : ""}
        </span>
        <span style={{ marginLeft: "auto" }}>faded = off-slice · shade = CL · hollow = cached</span>
      </div>
    </div>
  );
}
